import { useState } from 'react';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import type { AlertEvent } from './MeterAlertHistoryCard';
import type { AlertHistoryEvent } from '../hooks/useMeterAlerts';
import { useMeterAlertHistory } from '../hooks/useMeterAlerts';
import { useTimezoneFormatter } from '../lib/timezone';

function getCategoryBadgeVariant(category: string): 'destructive' | 'default' {
  return category === 'leak' || category === 'backflow' ? 'destructive' : 'default';
}

function getEventTypeBadgeVariant(
  eventType: string,
): 'destructive' | 'default' | 'secondary' | 'outline' {
  switch (eventType) {
    case 'detected':
      return 'destructive';
    case 'ended':
    case 'cleared':
      return 'secondary';
    default:
      return 'outline';
  }
}

function formatKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function isExpandable(value: unknown): value is Record<string, unknown> | unknown[] {
  return value !== null && typeof value === 'object';
}

function JsonTreeNode({ label, value, defaultOpen = false }: {
  label: string;
  value: unknown;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  if (isExpandable(value)) {
    const entries = Array.isArray(value)
      ? value.map((v, i) => [String(i), v] as const)
      : Object.entries(value);

    if (entries.length === 0) {
      return (
        <div className='flex items-center gap-1 py-0.5'>
          <span className='font-medium text-muted-foreground'>{formatKey(label)}</span>
          <span className='font-mono text-xs text-muted-foreground'>
            {Array.isArray(value) ? '[]' : '(empty)'}
          </span>
        </div>
      );
    }

    return (
      <div>
        <button
          type='button'
          className='flex items-center gap-1 py-0.5 hover:bg-muted/50 rounded -ml-1 px-1'
          onClick={() => setOpen(!open)}
        >
          {open
            ? <ChevronDown className='h-3 w-3 text-muted-foreground shrink-0' />
            : <ChevronRight className='h-3 w-3 text-muted-foreground shrink-0' />}
          <span className='font-medium text-muted-foreground'>{formatKey(label)}</span>
          <span className='text-xs text-muted-foreground ml-1'>
            {Array.isArray(value) ? `[${entries.length}]` : ''}
          </span>
        </button>
        {open && (
          <div className='ml-4 border-l pl-3'>
            {entries.map(([k, v]) => <JsonTreeNode key={k} label={k} value={v} />)}
          </div>
        )}
      </div>
    );
  }

  let display: string;
  if (value === null) display = 'null';
  else if (value === undefined) display = 'undefined';
  else if (typeof value === 'boolean') display = String(value);
  else if (typeof value === 'number') display = value.toLocaleString();
  else if (typeof value === 'string' && value === '') display = '""';
  else display = String(value);

  return (
    <div className='flex items-baseline gap-2 py-0.5'>
      <span className='font-medium text-muted-foreground shrink-0'>{formatKey(label)}</span>
      <span className='font-mono text-xs break-all'>{display}</span>
    </div>
  );
}

function MetricsTree({ metrics }: { metrics: Record<string, unknown> }) {
  const entries = Object.entries(metrics);
  if (entries.length === 0) {
    return <p className='text-sm text-muted-foreground'>No metrics data</p>;
  }

  return (
    <div className='text-sm space-y-0.5'>
      {entries.map(([key, value]) => (
        <JsonTreeNode key={key} label={key} value={value} defaultOpen />
      ))}
    </div>
  );
}

function HistoryTable(
  { events, timezoneOverride }: {
    events: AlertHistoryEvent[];
    timezoneOverride: string | null;
  },
) {
  // `createdAt` is a server-side processed timestamp (UTC) — tz-shifted.
  // `startTime` / `endTime` describe the real-world event window in meter
  // local time and are always shown in meter time.
  const { formatDateTime } = useTimezoneFormatter(timezoneOverride);
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Event Type</TableHead>
          <TableHead>Start Time</TableHead>
          <TableHead>End Time</TableHead>
          <TableHead>Created At</TableHead>
          <TableHead>Description</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {events.map((event, i) => (
          <TableRow key={i}>
            <TableCell>
              <Badge variant={getEventTypeBadgeVariant(event.eventType)}>
                {event.eventType}
              </Badge>
            </TableCell>
            <TableCell>
              {event.startTime ? new Date(event.startTime).toLocaleString() : <span>&mdash;</span>}
            </TableCell>
            <TableCell>
              {event.endTime ? new Date(event.endTime).toLocaleString() : <span>&mdash;</span>}
            </TableCell>
            <TableCell>
              {formatDateTime(new Date(event.createdAt))}
            </TableCell>
            <TableCell className='max-w-xs'>
              <span title={event.description}>
                {event.description.length > 120
                  ? `${event.description.slice(0, 120)}…`
                  : event.description}
              </span>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

interface MeterAlertDetailModalProps {
  alert: AlertEvent | null;
  sensorId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  timezoneOverride: string | null;
}

export function MeterAlertDetailModal(
  { alert, sensorId, open, onOpenChange, timezoneOverride }: MeterAlertDetailModalProps,
) {
  const { data: historyData, isLoading: historyLoading } = useMeterAlertHistory(
    sensorId,
    alert?.alertTypeId ?? null,
    alert?.startTime ?? null,
    alert?.createdAt ?? null,
  );

  if (!alert) return null;

  const isActive = !['ended', 'cleared'].includes(alert.eventType);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-5xl max-h-[85vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Badge variant={getCategoryBadgeVariant(alert.category)}>
              {alert.categoryDisplayName}
            </Badge>
            <span>{alert.subtypeDisplayName}</span>
          </DialogTitle>
          <DialogDescription>
            {alert.customerName} &middot; {isActive ? 'Active' : 'Ended'}
          </DialogDescription>
        </DialogHeader>

        {/* Section 1: Current Alert Details */}
        <div className='space-y-4'>
          <div className='flex items-center gap-2'>
            <Badge variant={isActive ? 'destructive' : 'secondary'}>
              {isActive ? 'active' : 'ended'}
            </Badge>
            <Badge variant='outline'>{alert.eventType}</Badge>
          </div>

          <div className='grid grid-cols-2 gap-4 text-sm'>
            <div>
              <span className='font-medium text-muted-foreground'>Start Time</span>
              <p>
                {alert.startTime ? new Date(alert.startTime).toLocaleString() : '—'}
              </p>
            </div>
            <div>
              <span className='font-medium text-muted-foreground'>End Time</span>
              <p>
                {alert.endTime ? new Date(alert.endTime).toLocaleString() : '—'}
              </p>
            </div>
          </div>

          {alert.description && (
            <div>
              <h4 className='text-sm font-medium text-muted-foreground mb-1'>Description</h4>
              <p className='text-sm'>{alert.description}</p>
            </div>
          )}

          <div>
            <h4 className='text-sm font-medium text-muted-foreground mb-2'>Metrics</h4>
            <MetricsTree metrics={alert.metrics} />
          </div>
        </div>

        {/* Section 2: Alert Event History */}
        <div className='border-t pt-4 mt-4'>
          <h3 className='text-sm font-semibold mb-3'>Alert Event History</h3>
          {historyLoading
            ? (
              <div className='flex items-center justify-center py-6'>
                <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
                <span className='ml-2 text-sm text-muted-foreground'>Loading history...</span>
              </div>
            )
            : historyData?.events && historyData.events.length > 0
            ? <HistoryTable events={historyData.events} timezoneOverride={timezoneOverride} />
            : (
              <p className='text-sm text-muted-foreground'>
                No event history available
              </p>
            )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
